"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.filterByUniqueGroupKey = exports.anyPredicate = exports.allPredicate = exports.getPredicateRule = exports.filterRecords = void 0;
const fast_memoize_1 = __importDefault(require("fast-memoize"));
const fields_1 = require("../utils/fields");
const fields_logic_registry_1 = require("../field-type-registry/fields-logic-registry");
const type_guards_1 = require("../utils/type-guards");
const filter_1 = require("../utils/filter");
/**
 * Generic filtering function. Returns filtered records according to specified rules.
 *
 * @param records              list of records
 * @param fields               application fields
 * @param operator             comparison type
 * @param conditions           filtering conditions
 * @param userPreferences      User preferences
 */
function filterRecords(records, fields, operator, conditions, userPreferences) {
    if (!conditions.length) {
        return records;
    }
    const operatorFn = operator === "or" ? anyPredicate : allPredicate;
    const predicates = conditions
        .map((condition) => getConditionPredicate(condition, fields, userPreferences))
        .filter(type_guards_1.isNotNullOrUndefined);
    return records.filter(operatorFn(predicates));
}
exports.filterRecords = filterRecords;
const getConditionPredicate = (condition, fields, userPreferences) => {
    if ((0, filter_1.isFilterGroup)(condition)) {
        const operatorFn = condition.operator === "or" ? anyPredicate : allPredicate;
        const groupPredicates = condition.conditions
            .map((groupCondition) => getConditionPredicate(groupCondition, fields, userPreferences))
            .filter(type_guards_1.isNotNullOrUndefined);
        return groupPredicates.length ? operatorFn(groupPredicates) : null;
    }
    return (0, exports.getPredicateRule)(fields, condition, userPreferences);
};
const getPredicateRule = (fields, rule, userPreferences) => {
    var _a;
    if (rule.comparison === "never") {
        return () => false;
    }
    const field = (0, fields_1.getAppFieldBySlug)(fields, rule.field);
    if (!field) {
        return null;
    }
    const currentField = (_a = field.parent) !== null && _a !== void 0 ? _a : field;
    const fieldLogic = (0, fields_logic_registry_1.getFieldLogic)(currentField.field_type);
    return fieldLogic === null || fieldLogic === void 0 ? void 0 : fieldLogic.makeRulePredicate(rule, currentField, userPreferences, fields);
};
exports.getPredicateRule = getPredicateRule;
/**
 * Returns a function for checking all record fields matches the specified filtering rules.
 * It is used for filtering record list.
 *
 * @param predicates     list of record predicate
 */
function allPredicate(predicates) {
    return (record) => {
        for (const predicate of predicates) {
            if (!predicate(record)) {
                return false;
            }
        }
        return true;
    };
}
exports.allPredicate = allPredicate;
/**
 * Returns a function for checking if one of the record fields matches the specified filtering rules.
 * It is used for filtering record list.
 *
 * @param predicates    list of record predicate
 */
function anyPredicate(predicates) {
    return (record) => {
        for (const predicate of predicates) {
            if (predicate(record)) {
                return true;
            }
        }
        return false;
    };
}
exports.anyPredicate = anyPredicate;
function filterByUniqueGroupKey(values, field, userPreferences) {
    const fieldLogic = (0, fields_logic_registry_1.getFieldLogic)(field.field_type);
    const groupKeyMap = new Map();
    const groupByKey = (0, fast_memoize_1.default)((value) => {
        return fieldLogic.groupByKey(value, field, userPreferences);
    });
    for (const value of values) {
        const groupKey = groupByKey(value);
        if (groupKeyMap.has(groupKey)) {
            continue;
        }
        groupKeyMap.set(groupKey, value);
    }
    return Array.from(groupKeyMap.values());
}
exports.filterByUniqueGroupKey = filterByUniqueGroupKey;
